import type { EnhancedReportDocument, NarrativeItem } from './enhancedNarrative'
import type { EnhancedNarrativeSlide } from '../../interfaces/reports-tab/enhanced-narrative'

export const REPORT_SLIDE_WIDTH = 1280
export const REPORT_SLIDE_HEIGHT = 720
export const REPORT_FONT = 'Arial'
export const REPORT_COLORS = {
    green: '#053D23', line: '#D6E4DA', card: '#EFF6F2', ink: '#1C2730', muted: '#4F5B63', chart: '#2F8057', grid: '#D6DBD8',
}

export type SlideElement =
    | { type: 'text'; x: number; y: number; width: number; text: string; size: number; bold: boolean; color: string; link?: string }
    | { type: 'rect'; x: number; y: number; width: number; height: number; fill: string; stroke?: string; radius?: number }
    | { type: 'line'; x: number; y: number; x2: number; y2: number; color: string; width: number }
    | { type: 'circle'; x: number; y: number; radius: number; fill: string }

export type ReportSlide = { title: string; elements: SlideElement[]; notes: string }
type MeasureText = (text: string, size: number, bold: boolean) => number

const fallbackMeasure: MeasureText = (text, size) => text.length * size * 0.55
export const createReportTextMeasurer = (): MeasureText => {
    const context = document.createElement('canvas').getContext('2d')
    if (!context) return fallbackMeasure
    return (text, size, bold) => {
        context.font = `${bold ? 'bold ' : ''}${size}px ${REPORT_FONT}`
        return context.measureText(text).width
    }
}

export const wrapReportText = (text: string, width: number, size: number, bold: boolean, measure: MeasureText): string[] => {
    const lines: string[] = []
    text.split('\n').forEach((paragraph) => {
        let line = ''
        paragraph.split(/\s+/).filter(Boolean).forEach((word) => {
            const candidate = line ? `${line} ${word}` : word
            if (measure(candidate, size, bold) <= width) {
                line = candidate
                return
            }
            if (line) {
                lines.push(line)
                line = ''
            }
            // Split long URLs and other unbroken strings as well as ordinary words.
            for (const character of word) {
                if (measure(line + character, size, bold) > width && line) {
                    lines.push(line)
                    line = ''
                }
                line += character
            }
        })
        if (line.trim()) lines.push(line.trim())
    })
    return lines
}

const dateLabel = (value?: string) => {
    if (!value) return 'Not provided'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? 'Not provided' : new Intl.DateTimeFormat('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
    }).format(date)
}

const missing = 'Not provided in this report.'

export const buildEnhancedNarrativeSlides = (
    document: EnhancedReportDocument,
    includeDetails = false,
    measure: MeasureText = fallbackMeasure,
): ReportSlide[] => {
    const c = REPORT_COLORS
    const slides: ReportSlide[] = []
    const confidence = [document.confidenceLevel && `Overall confidence: ${document.confidenceLevel}.`, document.confidenceRationale].filter(Boolean).join(' ')
    // Keep complete source text available even when the three-slide summary needs excerpts.
    const details: Array<{ layout: EnhancedNarrativeSlide['layout']; title: string; items: NarrativeItem[] }> = [
        { layout: 'facts-and-radar', title: document.overviewTitle, items: [
            { title: `Maturity: ${document.maturity || 'Not provided'}`, text: document.summary },
            { title: 'Strongest assets', text: document.strongestAssets.map((asset) => `• ${asset}`).join('\n') },
            { title: 'Primary risk', text: document.primaryRisk },
            { title: 'Confidence', text: confidence },
            { title: document.radarTitle, text: document.radarNote },
        ] },
        { layout: 'issues-and-competitors', title: document.issuesTitle, items: [
            ...document.keyIssues,
            { title: 'Competitive positioning', text: document.competitivePositioning },
            ...document.responses,
        ] },
        { layout: 'three-horizon-action-plan', title: document.planTitle, items: [
            ...document.actionGroups.flatMap((group) => [
                { title: `${group.timeframe} — ${group.focus}`, text: '' },
                ...group.items.map((item, index) => ({ title: `${index + 1}. ${item.title}`, text: item.text })),
            ]),
            { title: 'Target outcome', text: document.targetOutcome },
        ] },
    ]
    const noteFor = (index: number) => {
        const detail = details[index]
        const note = document.speakerNotes.find((entry) => entry.layout === detail.layout)
        return [
            note?.notes,
            ...detail.items.map((item) => [item.title, item.text].filter(Boolean).join('\n')),
            ...(index === 0 ? document.scores.map((score) => `${score.label}: ${score.score ?? 'Not scored'}\n${score.evidence}`) : []),
            ...(note?.sources || []),
        ].filter(Boolean).join('\n\n')
    }
    const text = (slide: ReportSlide, value: string, x: number, y: number, width: number, size = 16, bold = false, color = c.ink, maxLines = Infinity, link?: string) => {
        let lines = wrapReportText(value, width, size, bold, measure)
        if (lines.length > maxLines) {
            lines = lines.slice(0, maxLines)
            let last = lines[lines.length - 1]
            while (last && measure(`${last}…`, size, bold) > width) last = last.slice(0, -1)
            lines[lines.length - 1] = `${last.trim()}…`
        }
        lines.forEach((line, index) => slide.elements.push({ type: 'text', x, y: y + index * size * 1.23, width, text: line, size, bold, color, link }))
        return lines.length * size * 1.23
    }
    const box = (slide: ReportSlide, x: number, y: number, width: number, height: number) => {
        slide.elements.push({ type: 'rect', x, y, width, height, fill: c.card, stroke: c.line, radius: 16 })
    }
    const createSlide = (title: string, subtitle: string, notes = ''): ReportSlide => {
        const slide: ReportSlide = { title, elements: [], notes: notes || `Property: ${document.propertyName}\n${document.sources.join('\n')}` }
        slide.elements.push({ type: 'rect', x: 0, y: 0, width: 1280, height: 14, fill: c.green })
        text(slide, title, 64, 49, 1152, measure(title, 34, true) > 1152 ? 28 : 34, true, c.green, 2)
        text(slide, subtitle, 64, 122, 1152, 14, false, c.muted, 2)
        slides.push(slide)
        return slide
    }
    const card = (slide: ReportSlide, title: string, content: string, x: number, y: number, width: number, height: number, size = 14) => {
        box(slide, x, y, width, height)
        const titleHeight = text(slide, title, x + 22, y + 20, width - 44, 17, true, c.green, 2)
        const available = height - titleHeight - 38
        let fontSize = size
        while (fontSize > 12 && wrapReportText(content, width - 44, fontSize, false, measure).length * fontSize * 1.23 > available) fontSize -= 0.5
        text(slide, content, x + 22, y + 24 + titleHeight, width - 44, fontSize, false, c.ink, Math.max(1, Math.floor(available / (fontSize * 1.23))))
    }

    // Slide 1 — currentSituation, confidence and the radarChart dimensions.
    const generated = document.report.generatedAt ? `Completed ${dateLabel(document.report.generatedAt)}` : ''
    const reviewDue = document.report.reviewDueAt ? `Review due ${dateLabel(document.report.reviewDueAt)}` : ''
    const overview = createSlide(
        document.overviewTitle,
        document.overviewSubtitle || ['Enhanced narrative report', generated, reviewDue].filter(Boolean).join('  |  '),
        noteFor(0),
    )
    card(overview, `Maturity: ${document.maturity.toUpperCase() || 'NOT PROVIDED'}`, document.summary || missing, 64, 165, 384, 138, 13.5)
    card(overview, 'Strongest assets', document.strongestAssets.map((asset) => `• ${asset}`).join('\n') || missing, 64, 315, 384, 150, 13.5)
    card(overview, 'Primary risk', document.primaryRisk || missing, 64, 477, 384, 126, 13.5)
    text(overview, document.radarTitle, 636, 148, 520, 16, true, c.green, 1)

    const axes = document.scores.length
    const span = document.radarMax - document.radarMin
    // Respect the supplied scale; cap grid density for unusually small steps.
    const ringCount = Math.min(20, Math.ceil(span / document.radarStep))
    const ringValues = Array.from({ length: ringCount }, (_, index) =>
        index === ringCount - 1 ? document.radarMax : document.radarMin + (index + 1) * Math.max(document.radarStep, span / 20))
    const cx = 856
    const cy = 364
    const radius = 164
    const distanceFor = (value: number) => radius * (value - document.radarMin) / span
    const point = (index: number, distance: number) => {
        const angle = -Math.PI / 2 + index * (2 * Math.PI) / Math.max(1, axes)
        return { x: cx + Math.cos(angle) * distance, y: cy + Math.sin(angle) * distance }
    }
    const line = (first: { x: number; y: number }, second: { x: number; y: number }, color: string, width: number) => {
        overview.elements.push({ type: 'line', x: first.x, y: first.y, x2: second.x, y2: second.y, color, width })
    }
    for (const value of ringValues) {
        for (let axis = 0; axis < axes; axis += 1) line(point(axis, distanceFor(value)), point((axis + 1) % axes, distanceFor(value)), c.grid, 0.8)
        if (axes) text(overview, String(Number(value.toFixed(2))), cx + 5, cy - distanceFor(value) - 5, 30, 10, false, c.muted)
    }
    document.scores.forEach((item, index) => {
        line({ x: cx, y: cy }, point(index, radius), c.grid, 0.8)
        const anchor = point(index, radius + 22)
        // Unscored dimensions keep their axis and label, but are never marked N/A or plotted as zero.
        const width = measure(item.label, 11, false)
        const centered = index === 0 || index * 2 === axes
        const x = centered ? anchor.x - width / 2 : index * 2 < axes ? anchor.x : anchor.x - width
        text(overview, item.label, x, anchor.y - 6, width + 2, 11, false, c.muted)
        if (item.score === null) return
        const here = point(index, distanceFor(item.score))
        const next = document.scores[(index + 1) % axes]
        if (next.score !== null) line(here, point((index + 1) % axes, distanceFor(next.score)), c.chart, 2.2)
        overview.elements.push({ type: 'circle', x: here.x, y: here.y, radius: 3, fill: c.chart })
    })
    const scored = document.scores.filter((item) => item.score !== null).length
    if (scored < axes) text(overview, `${scored}/${axes} dimensions scored. Unscored dimensions are not plotted.`, 500, 567, 710, 11, false, c.muted, 1)
    text(overview, document.radarNote, 500, 584, 710, 11, false, c.muted, 3)
    text(overview, confidence || missing, 72, 635, 1136, 13, false, c.ink, 2)

    // Slide 2 — keyIssues, competitorPositioning and the three responses.
    const issues = createSlide(
        document.issuesTitle,
        document.issuesSubtitle || 'The main issues, competitive context and recommended responses identified in this diagnostic.',
        noteFor(1),
    )
    for (let index = 0; index < 3; index += 1) {
        const issue = document.keyIssues[index]
        card(issues, issue?.title || `Key issue ${index + 1}`, issue?.text || missing, 64 + index * 392, 168, 368, 206, 15)
    }
    card(issues, 'Competitive positioning', document.competitivePositioning || missing, 64, 400, 1152, 132, 15)
    for (let index = 0; index < 3; index += 1) {
        const response = document.responses[index]
        card(issues, response?.title || `Response ${index + 1}`, response?.text || missing, 64 + index * 392, 552, 368, 105, 13)
    }

    // Slide 3 — shortTerm, midTerm and longTerm actions with their rationales.
    const actions = createSlide(
        document.planTitle,
        document.planSubtitle || 'Priorities for the first 30 days, the next 90 days and longer-term improvements.',
        noteFor(2),
    )
    document.actionGroups.forEach((group, index) => {
        const x = 64 + index * 392
        box(actions, x, 168, 368, 440)
        text(actions, group.timeframe.toUpperCase(), x + 22, 189, 324, 15, true, c.green, 1)
        text(actions, group.focus, x + 22, 209, 324, 12, false, c.muted, 2)
        // Fit complete actions and rationales where possible, preserving overflow in full details.
        const visible = group.items.slice(0, 5)
        if (!visible.length) text(actions, missing, x + 22, 248, 324, 14)
        const available = 344
        let titleSize = 13
        const bodySize = () => Math.max(10.5, titleSize - 2)
        const itemHeight = (item: NarrativeItem, index: number) =>
            wrapReportText(`${index + 1}. ${item.title}`, 324, titleSize, true, measure).length * titleSize * 1.23 +
            (item.text ? wrapReportText(item.text, 312, bodySize(), false, measure).length * bodySize() * 1.23 + 3 : 0)
        const totalHeight = () => visible.reduce((sum, item, index) => sum + itemHeight(item, index), 0) + Math.max(0, visible.length - 1) * 9
        while (titleSize > 12 && totalHeight() > available) titleSize -= 0.5
        const fits = totalHeight() <= available
        const budget = (available - Math.max(0, visible.length - 1) * 9) / Math.max(1, visible.length)
        let y = 248
        visible.forEach((item, itemIndex) => {
            const used = text(actions, `${itemIndex + 1}. ${item.title}`, x + 22, y, 324, titleSize, true, c.ink, fits ? Infinity : Math.max(1, Math.floor(budget * 0.65 / (titleSize * 1.23))))
            const remaining = fits ? Infinity : Math.max(0, Math.floor((budget - used - 3) / (bodySize() * 1.23)))
            const rationale = remaining && item.text ? text(actions, item.text, x + 34, y + used + 3, 312, bodySize(), false, c.muted, remaining) + 3 : 0
            y += used + rationale + 9
        })
    })
    actions.elements.push({ type: 'rect', x: 64, y: 620, width: 1152, height: 54, fill: c.green, radius: 7 })
    text(actions, 'Target outcome', 80, 628, 220, 13, true, '#FFFFFF', 1)
    text(actions, document.targetOutcome || reviewDue || missing, 80, 646, 1120, 11.5, false, '#FFFFFF', 2)

    if (includeDetails) {
        details.forEach((detail) => {
            const note = document.speakerNotes.find((entry) => entry.layout === detail.layout)
            let slide = createSlide(detail.title, 'Full report details')
            let y = 173
            const newPage = () => { slide = createSlide(`${detail.title} (continued)`, 'Full report details'); y = 173 }
            const items = [...detail.items, ...(note?.notes ? [{ title: 'Speaker notes', text: note.notes }] : [])]
            items.forEach((item) => {
                if (!item.title && !item.text) return
                const headingLines = wrapReportText(item.title, 1136, 15, true, measure)
                const bodyLines = wrapReportText(item.text, 1136, 14, false, measure)
                const blockHeight = headingLines.length * 20 + bodyLines.length * 18
                const requiredSpace = blockHeight <= 462 ? blockHeight : Math.min(headingLines.length, 3) * 20 + (item.text ? 18 : 0)
                if (y + requiredSpace > 635) newPage()
                headingLines.forEach((heading) => {
                    if (y > 615) newPage()
                    text(slide, heading, 72, y, 1136, 15, true, c.green)
                    y += 20
                })
                bodyLines.forEach((line) => {
                    if (y > 630) newPage()
                    text(slide, line, 72, y, 1136, 14)
                    y += 18
                })
                y += 12
            })
        })
        if (document.scores.some((item) => item.evidence)) {
            let slide = createSlide('Scorecard evidence', document.propertyName)
            let y = 173
            document.scores.forEach((item) => {
                const heading = `${item.label} — ${item.score === null ? 'not scored' : `${item.score}/${document.radarMax}`}`
                if (y > 600) { slide = createSlide('Scorecard evidence (continued)', document.propertyName); y = 173 }
                text(slide, heading, 72, y, 1136, 15, true, c.green, 1)
                y += 20
                for (const evidenceLine of wrapReportText(item.evidence || missing, 1136, 13, false, measure)) {
                    if (y > 634) { slide = createSlide('Scorecard evidence (continued)', document.propertyName); y = 173 }
                    text(slide, evidenceLine, 72, y, 1136, 13)
                    y += 17
                }
                y += 10
            })
        }
        if (document.sources.length) {
            let slide = createSlide('Sources', document.propertyName)
            let y = 173
            document.sources.forEach((url, index) => {
                for (const sourceLine of wrapReportText(`${index + 1}. ${url}`, 1136, 14, false, measure)) {
                    if (y > 627) { slide = createSlide('Sources (continued)', document.propertyName); y = 173 }
                    text(slide, sourceLine, 72, y, 1136, 14, false, c.green, Infinity, url)
                    y += 19
                }
                y += 12
            })
        }
    }
    slides.forEach((slide, index) => {
        text(slide, `HOTELWORLD AI  |  ${document.propertyName.toUpperCase()}  |  ${index + 1}/${slides.length}`, 64, 690, 775, 10, false, c.muted, 1)
        if (index < 3) text(slide, 'Executive summary · Full detail available in the full report', 863, 690, 353, 10, false, c.muted, 1)
    })
    return slides
}

const escapeXml = (value: string) => value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[character] || character))

// A single scene powers the preview and both download formats, including text wrapping and chart geometry.
export const renderReportSlideSvg = (slide: ReportSlide): string => {
    const elements = slide.elements.map((element) => {
        if (element.type === 'text') return `<text x="${element.x}" y="${element.y + element.size * 0.9}" font-family="${REPORT_FONT}" font-size="${element.size}" font-weight="${element.bold ? 700 : 400}" fill="${element.color}">${escapeXml(element.text)}</text>`
        if (element.type === 'rect') return `<rect x="${element.x}" y="${element.y}" width="${element.width}" height="${element.height}" rx="${element.radius || 0}" fill="${element.fill}" stroke="${element.stroke || 'none'}"/>`
        if (element.type === 'line') return `<line x1="${element.x}" y1="${element.y}" x2="${element.x2}" y2="${element.y2}" stroke="${element.color}" stroke-width="${element.width}"/>`
        return `<circle cx="${element.x}" cy="${element.y}" r="${element.radius}" fill="${element.fill}"/>`
    }).join('')
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720" role="img"><title>${escapeXml(slide.title)}</title><rect width="1280" height="720" fill="#FFFFFF"/>${elements}</svg>`
}
